import { LoadingButton } from '@mui/lab';
import { Box, Button, InputAdornment, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Dispatch, FC, SetStateAction } from 'react';
import BasicModal from '../../../common/BasicModal';
import { Add, Delete } from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';

type Props = {
	label: string;
	openModal: boolean;
	data: any;
	isSocial?: boolean;
	setOpen: Dispatch<SetStateAction<boolean>>;
	setData: Dispatch<SetStateAction<any>>;
	handleCreate: () => void;
};

const MediaEditModal: FC<Props> = ({ openModal, setOpen, handleCreate, label, setData, data, isSocial = false }) => {
	let { t } = useTranslation();
	const handleOpenModal = () => {
		setOpen(!openModal);
	};

	return (
		<BasicModal
			open={!!openModal}
			onClose={handleOpenModal}
			title={t(label)}
			titleClassName={'text-center grow '}
			subClassName={'flex flex-row-reverse'}
			className={'max-w-screen-md rounded-lg'}
			showBackIcon={false}
			action={
				<div className="w-full">
					<LoadingButton onClick={handleCreate} variant="contained" color="primary" className="mx-2">
						{t('register')}
					</LoadingButton>
					<Button onClick={handleOpenModal} variant="outlined" className="mx-2" color="error">
						{t('cancel')}
					</Button>
				</div>
			}
		>
			<Box className="mb-5 mt-6 flex flex-wrap justify-between gap-4">
				<Typography className="w-full">{t('total_selectable_title_media')}</Typography>
				<TextField variant="outlined" InputLabelProps={{ shrink: data?.unit }} className="mb-2 w-[48%]" value={data?.unit} onChange={e => setData({ ...data, unit: e.target.value })} label={t('unit')} size="medium" />

				{data?.types?.map((item: any, index: number) => (
					<>
						<Typography className="w-full">
							{t('Type_of_advertising')} {item.name}
						</Typography>
						<TextField
							variant="outlined"
							className={`mb-2 w-[48%]`}
							value={item?.messages?.title ?? ''}
							InputLabelProps={{ shrink: item?.messages?.title }}
							onChange={e =>
								setData({
									...data,
									types: data?.types?.map((d: any, index2: number) => (index2 === index ? { ...d, messages: { ...d.messages, title: e.target.value } } : d)),
								})
							}
							label={t('title')}
							size="medium"
						/>
						<TextField
							variant="outlined"
							className={`mb-2 w-[48%]`}
							value={item?.messages?.sub_title ?? ''}
							InputLabelProps={{ shrink: item?.messages?.sub_title }}
							onChange={e =>
								setData({
									...data,
									types: data?.types?.map((d: any, index2: number) => (index2 === index ? { ...d, messages: { ...d.messages, sub_title: e.target.value } } : d)),
								})
							}
							label={t('description')}
							size="medium"
						/>
						<Typography className="w-full">
							{t('rules')} ({item.name})
						</Typography>
						<TextField
							variant="outlined"
							className={`mb-2 w-full`}
							value={item?.rules ?? ''}
							InputLabelProps={{ shrink: item?.rules }}
							onChange={e =>
								setData({
									...data,
									types: data?.types?.map((d: any, index2: number) => (index2 === index ? { ...d, rules: [e.target.value] } : d)),
								})
							}
							label={t('title')}
							size="medium"
						/>
						<Typography className="w-full">{`${t('edit_media')} (${item.name})`}</Typography>
						<TextField
							variant="outlined"
							className={`mb-2 w-full`}
							value={item?.description ?? ''}
							InputLabelProps={{ shrink: item?.description }}
							onChange={e =>
								setData({
									...data,
									types: data?.types?.map((d: any, index2: number) => (index2 === index ? { ...d, description: e.target.value } : d)),
								})
							}
							label={t('description')}
							size="medium"
						/>
						<Typography className="w-full">{`${t('description_of_the_media_type')} (${item.name})`}</Typography>
						{item?.sub_types?.map((subItem: any, index2: number) => (
							<>
								<TextField
									variant="outlined"
									className="mb-2 w-[48%]"
									multiline
									rows={3}
									value={subItem?.title}
									InputLabelProps={{ shrink: subItem?.title }}
									onChange={e =>
										setData({
											...data,
											types: data?.types?.map((d: any, iindex2: number) => (iindex2 === index ? { ...d, sub_types: d.sub_types?.map((ss: any, iindex: number) => (index2 === iindex ? { ...ss, title: e.target.value } : ss)) } : d)),
										})
									}
									label={`${t('title')} ${t(subItem.title)}`}
									size="medium"
								/>
								<TextField
									variant="outlined"
									className="mb-2 w-[48%]"
									multiline
									rows={3}
									value={subItem?.description}
									InputLabelProps={{ shrink: subItem?.description }}
									onChange={e =>
										setData({
											...data,
											types: data?.types?.map((d: any, iindex2: number) => (iindex2 === index ? { ...d, sub_types: d.sub_types?.map((ss: any, iindex: number) => (index2 === iindex ? { ...ss, description: e.target.value } : ss)) } : d)),
										})
									}
									label={`${t('description')} ${t(subItem.title)}`}
									size="medium"
								/>
							</>
						))}
					</>
				))}
				{!isSocial && <TextField variant="outlined" className="mb-2 w-[48%]" onChange={e => setData({ ...data, entities_count: e.target.value })} placeholder={t('entities_count')} size="medium" />}

				<Typography className="w-full">{t('media_regulation_rules')}</Typography>
				{data?.rules?.map((r: any, rIndex: number) => (
					<>
						<TextField
							key={rIndex}
							variant="outlined"
							className="mb-2 w-full"
							value={r}
							InputLabelProps={{ shrink: r }}
							onChange={e =>
								setData({
									...data,
									rules: data?.rules?.map((r: any, rulesIndex: number) => (rulesIndex === rIndex ? e.target.value : r)),
								})
							}
							placeholder={t('rules')}
							size="medium"
							InputProps={
								rIndex === data?.rules?.length - 1
									? {
											endAdornment: (
												<InputAdornment position="end">
													<IconButton
														onClick={() => {
															setData({
																...data,
																rules: data?.rules.slice(0, data?.rules.length - 1),
															});
														}}
													>
														<Delete />
													</IconButton>
												</InputAdornment>
											),
										}
									: {}
							}
						/>
					</>
				))}

				<Button
					variant={'outlined'}
					onClick={() => {
						setData({
							...data,
							rules: [...data?.rules, ''],
						});
					}}
				>
					<Add />
				</Button>
			</Box>
		</BasicModal>
	);
};

export default MediaEditModal;
